// src/Hooks/useImportExcelFirebase.js

import { db, storage } from '../Firebase/firebase';
import { ref, deleteObject, listAll } from 'firebase/storage';
import {
  collection,
  writeBatch,
  doc,
  query,
  where,
  getDocs,
  deleteDoc,
  getDoc,
} from 'firebase/firestore';

import { useImportExcelListeners } from './useImportExcelListeners';
import { useImportExcelBatchSave } from './useImportExcelBatchSave';

const yieldToMain = () => new Promise((resolve) => setTimeout(resolve, 15));

export const useImportExcelFirebase = (stateStore) => {
  const { state, setters } = stateStore;

  const { handlePasteMenu, handleSaveMenu } = useImportExcelListeners(stateStore);
  const { executeSave } = useImportExcelBatchSave(stateStore);

  const executeDeleteBatches = async () => {
    const { selectedHistoryIds, sessionBatches } = state;
    if (selectedHistoryIds.length === 0) return;

    setters.setUploadProgress({ current: 0, total: 0 });
    setters.setIsProcessing(true);
    setters.setProgressMessage('Reversing import & cleaning storage...');
    setters.setShowHistoryDeleteConfirm(false);

    await yieldToMain();

    try {
      let totalDeleted = 0;

      for (const batchId of selectedHistoryIds) {
        const groupsQuery = query(
          collection(db, 'markerGroups'),
          where('importBatchId', '==', batchId),
        );
        const groupsSnapshot = await getDocs(groupsQuery);

        const markersQuery = query(
          collection(db, 'markers'),
          where('importBatchId', '==', batchId),
        );
        const markersSnapshot = await getDocs(markersQuery);

        const polygonsQuery = query(
          collection(db, 'polygons'),
          where('importBatchId', '==', batchId),
        );
        const polygonsSnapshot = await getDocs(polygonsQuery);

        const polylinesQuery = query(
          collection(db, 'polylines'),
          where('importBatchId', '==', batchId),
        );
        const polylinesSnapshot = await getDocs(polylinesQuery);

        const storageDeletePromises = [];

        groupsSnapshot.docs.forEach((docSnap) => {
          const groupId = docSnap.id;

          const hoverFolderRef = ref(storage, `marker_images/${groupId}`);
          const hoverFolderDeletePromise = listAll(hoverFolderRef)
            .then((res) => {
              const itemDeletes = res.items.map((itemRef) =>
                deleteObject(itemRef).catch(() => {}),
              );
              return Promise.all(itemDeletes);
            })
            .catch(() => {});
          storageDeletePromises.push(hoverFolderDeletePromise);

          const photosFolderRef = ref(storage, `photos/${groupId}`);
          const folderDeletePromise = listAll(photosFolderRef)
            .then((res) => {
              const itemDeletes = res.items.map((itemRef) =>
                deleteObject(itemRef).catch(() => {}),
              );
              return Promise.all(itemDeletes);
            })
            .catch(() => {});
          storageDeletePromises.push(folderDeletePromise);

          const docsFolderRef = ref(storage, `documents/${groupId}`);
          const docsFolderDeletePromise = listAll(docsFolderRef)
            .then((res) => {
              const itemDeletes = res.items.map((itemRef) =>
                deleteObject(itemRef).catch(() => {}),
              );
              return Promise.all(itemDeletes);
            })
            .catch(() => {});
          storageDeletePromises.push(docsFolderDeletePromise);
        });

        if (storageDeletePromises.length > 0) {
          await Promise.all(storageDeletePromises);
        }

        const allDocsToDelete = [
          ...groupsSnapshot.docs,
          ...markersSnapshot.docs,
          ...polygonsSnapshot.docs,
          ...polylinesSnapshot.docs,
        ];
        const chunkSize = 400;

        for (let i = 0; i < allDocsToDelete.length; i += chunkSize) {
          const chunk = allDocsToDelete.slice(i, i + chunkSize);
          const deleteBatch = writeBatch(db);
          chunk.forEach((d) => deleteBatch.delete(d.ref));
          await deleteBatch.commit();
          await yieldToMain();
        }

        totalDeleted += groupsSnapshot.docs.length;
        await deleteDoc(doc(db, 'excelImports', batchId));
      }

      const idsToUnblock = [];
      selectedHistoryIds.forEach((batchId) => {
        if (sessionBatches[batchId]) {
          idsToUnblock.push(...sessionBatches[batchId]);
        }
      });
      setters.setSuccessfullyImportedIds((prev) =>
        prev.filter((id) => !idsToUnblock.includes(id)),
      );

      setters.setAlertState({
        open: true,
        message: `Successfully deleted ${totalDeleted} markers and reversed import.`,
        severity: 'success',
      });
      setters.setSelectedHistoryIds([]);
    } catch (error) {
      console.error('Error deleting batch:', error);
      setters.setAlertState({
        open: true,
        message: `Failed to reverse import: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
      setters.setProgressMessage('');
    }
  };

  const executePublishBatches = async () => {
    const { selectedHistoryIds } = state;
    setters.setUploadProgress({ current: 0, total: 0 });
    setters.setIsProcessing(true);
    setters.setProgressMessage('Archiving batches...');

    await yieldToMain();

    try {
      const updateBatch = writeBatch(db);

      for (const batchId of selectedHistoryIds) {
        const docRef = doc(db, 'excelImports', batchId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          const archiveRef = doc(db, 'excelArchives', batchId);

          updateBatch.set(archiveRef, data);
          updateBatch.delete(docRef);
        }
      }

      await updateBatch.commit();

      setters.setAlertState({
        open: true,
        message: `Success! ${selectedHistoryIds.length} import batches have been archived.`,
        severity: 'success',
      });
      setters.setSelectedHistoryIds([]);
    } catch (error) {
      console.error('Error publishing batch:', error);
      setters.setAlertState({
        open: true,
        message: `Failed to archive batches: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
      setters.setProgressMessage('');
    }
  };

  return {
    handleSaveMenu,
    executeDeleteBatches,
    executePublishBatches,
    executeSave,
    handlePasteMenu,
  };
};
